import { lazy, Suspense } from 'react'

import LinearProgress, {
	LinearProgressProps,
} from '@mui/material/LinearProgress'
import { styled } from '@mui/material/styles'
import Head from 'next/head'
import { IUser } from 'interfaces/user'

const Header = lazy(() => import('./header'))
const FooterBlock = lazy(() => import('./FooterBlock'))

const LoaderWrapper = styled('div')(({ theme }) => ({
	position: 'fixed',
	top: 0,
	left: 0,
	zIndex: 2001,
	width: '100%',
	'& > * + *': {
		marginTop: theme.spacing(2),
	},
}))

export type LoaderProps = LinearProgressProps

const Loader = () => (
	<LoaderWrapper>
		<LinearProgress
			color="primary"
			role="progressbar"
			aria-label="Loading page content"
		/>
	</LoaderWrapper>
)

interface Props {
	title?: string
	description?: string
	keywords?: string
	canonical?: string
	isLoggedIn?: boolean
	user?: IUser | null
}

export default function Layout({
	children,
	title,
	description: descriptionProp,
	keywords: keywordsProp,
	canonical,
	isLoggedIn = false,
	user,
}: React.PropsWithChildren<Props>) {
	const fullTitle = title ? `ASTA | ${title}` : 'ASTA'
	const description =
		descriptionProp ||
		'ASTA: ASTA performs fully autonomous functional testing of enterprise applications.'
	const keywords =
		keywordsProp || 'ASTA, Testing, Autonomous, Software Testing Agent'
	return (
		<>
			<Head>
				<title>{fullTitle}</title>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0"
				/>
				<meta name="keywords" content={keywords} />
				<meta name="description" content={description} />
				<meta property="og:site_name" content="ASTA" />
				<meta property="og:title" content={fullTitle} />
				<meta property="og:description" content={description} />
				<meta name="twitter:title" content={fullTitle} />
				<meta name="twitter:description" content={description} />
				{canonical && <link rel="canonical" href={canonical} />}
			</Head>
			<Suspense fallback={<Loader />}>
				<Header isLoggedIn={isLoggedIn} user={user} />
				<main id="main-content" role="main">
					{children}
				</main>
				<FooterBlock />
			</Suspense>
		</>
	)
}
